import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

type Variant = "primary" | "secondary" | "ghost" | "destructive";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

/**
 * Base button — shadcn-derived.
 *
 * Per `AGENTS.md §3 #15` (no Material patterns):
 *   - 4-step elevation shadow scale is banned; this button uses a single
 *     hairline + 1 shadow step.
 *   - No ripple effect.
 *   - No FAB variant.
 *
 * Touch target: minimum 44×44 px via `.tap-target` class.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    leadingIcon,
    trailingIcon,
    className,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      data-variant={variant}
      data-size={size}
      className={cn(
        "tap-target inline-flex items-center justify-center gap-2",
        // Apple HIG signature: full-pill primary action shape + press feedback
        // (transform scale(0.97) on active).  Per DESIGN.md refactor 2026-09-22.
        "press font-medium rounded-full transition-colors",
        "duration-quick ease-paper",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "select-none whitespace-nowrap",
        // size
        size === "sm" && "h-9 px-3 text-body-sm",
        size === "md" && "h-11 px-4 text-body",
        size === "lg" && "h-12 px-5 text-body",
        // variant
        variant === "primary" && "bg-you-strong text-paper hover:bg-you",
        variant === "secondary" &&
          "bg-paper-deep text-ink-1 hover:bg-paper-calm border border-hairline",
        variant === "ghost" && "bg-transparent text-ink-1 hover:bg-black/5",
        variant === "destructive" && "bg-partner-strong text-paper hover:bg-partner",
        className,
      )}
      {...rest}
    >
      {leadingIcon ? (
        <span className="shrink-0" aria-hidden>
          {leadingIcon}
        </span>
      ) : null}
      <span>{children}</span>
      {trailingIcon ? (
        <span className="shrink-0" aria-hidden>
          {trailingIcon}
        </span>
      ) : null}
    </button>
  );
});
